import React, { useEffect, useRef, useState, useCallback } from 'react';
import {
  Box,
  Typography,
  IconButton,
  TextField,
  Button,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Autocomplete,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormHelperText,
  useTheme,
} from '@mui/material';
import {
  ContactRound,
  X,
  Building2,
  UsersRound,
  Search,
  ChevronDown,
  Plus,
  CirclePlus,
  CircleAlert,
} from 'lucide-react';
import { useNuevoCliente } from '../hooks/useNuevoCliente';
import { ContactCard } from './ContactCard';
import { ContactoEditor } from './ContactoEditor';
import { ActividadEconomica, Ciudad, ClienteCreado, ClienteDetalle, Departamento } from '../types';
import { ubicacionService } from '../services/ubicacionService';
import { actividadesService } from '../services/actividadesService';

interface NuevoClienteDrawerProps {
  open: boolean;
  onClose: () => void;
  onCreado?: (cliente?: ClienteCreado) => void;
  clienteEditar?: ClienteDetalle | null;
  onActualizado?: () => void;
}

export function NuevoClienteDrawer({ open, onClose, onCreado, clienteEditar, onActualizado }: NuevoClienteDrawerProps) {
  const theme = useTheme();
  // El panel se construye a mano (no usa Drawer/Modal de MUI), así que su zIndex
  // debe quedar por debajo de theme.zIndex.modal para que los overlays internos
  // (Autocomplete, Select, Dialog) reciban los clics en vez del panel estático.
  const drawerZIndex = theme.zIndex.drawer;
  const hook = useNuevoCliente(open, onClose, onCreado, clienteEditar, onActualizado);
  const [departamentos, setDepartamentos] = useState<Departamento[]>([]);
  const [ciudades, setCiudades] = useState<Ciudad[]>([]);
  const [actividades, setActividades] = useState<ActividadEconomica[]>([]);
  const razonSocialRef = useRef<HTMLInputElement>(null);
  const drawerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    actividadesService.listar().then(setActividades).catch(() => {});
    ubicacionService.listarDepartamentos().then(setDepartamentos).catch(() => {});
  }, []);

  useEffect(() => {
    if (hook.empresa.departamentoId) {
      ubicacionService
        .listarCiudades(hook.empresa.departamentoId)
        .then(setCiudades)
        .catch(() => setCiudades([]));
    } else {
      setCiudades([]);
    }
  }, [hook.empresa.departamentoId]);

  useEffect(() => {
    if (open) {
      setTimeout(() => razonSocialRef.current?.focus(), 50);
    }
  }, [open]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Escape') {
        hook.intentarCerrar();
      }
    },
    [hook]
  );

  if (!open) {
    return null;
  }

  const actividadSeleccionada =
    actividades.find((a) => a.id === hook.empresa.actividadEconomicaId) ?? null;
  const ciudadSeleccionada = ciudades.find((c) => c.id === hook.empresa.ciudadId) ?? null;

  return (
    <>
      {/* Overlay */}
      <Box
        onClick={hook.intentarCerrar}
        sx={{
          position: 'fixed',
          inset: 0,
          backgroundColor: 'rgba(18,18,22,0.45)',
          zIndex: drawerZIndex,
        }}
        aria-hidden="true"
      />

      {/* Drawer */}
      <Box
        ref={drawerRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="new-client-title"
        onKeyDown={handleKeyDown}
        sx={{
          position: 'fixed',
          right: 0,
          top: 0,
          height: '100dvh',
          width: 'min(1022px, 100vw)',
          backgroundColor: '#fff',
          boxShadow: '-8px 0 28px rgba(18,18,22,0.10)',
          zIndex: drawerZIndex + 1,
          display: 'flex',
          flexDirection: 'column',
          animation: 'slideInDrawer 240ms cubic-bezier(0.22,1,0.36,1) both',
          '@keyframes slideInDrawer': {
            from: { transform: 'translateX(100%)' },
            to: { transform: 'translateX(0)' },
          },
        }}
      >
        {/* Header sticky */}
        <Box
          sx={{
            minHeight: 118,
            flexShrink: 0,
            backgroundColor: '#FBF5FA',
            borderBottom: '1px solid #E8CFE2',
            px: '28px',
            py: '24px',
            display: 'grid',
            gridTemplateColumns: '68px 1fr auto',
            alignItems: 'center',
            gap: '16px',
          }}
        >
          <Box
            sx={{
              width: 68,
              height: 68,
              borderRadius: '50%',
              backgroundColor: '#963781',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <ContactRound size={32} color="#fff" />
          </Box>

          <Box>
            <Typography
              id="new-client-title"
              sx={{ fontWeight: 700, fontSize: '1.25rem', fontFamily: 'Poppins, sans-serif', color: 'text.primary' }}
            >
              {hook.modoEdicion ? 'Editar cliente' : 'Nuevo cliente'}
            </Typography>
            <Typography sx={{ fontSize: '0.85rem', color: 'text.secondary', fontFamily: 'Poppins, sans-serif', mt: 0.25 }}>
              {hook.modoEdicion
                ? 'Modifica la información del cliente y sus contactos'
                : 'Completa la información comercial y sus contactos'}
            </Typography>
          </Box>

          <IconButton
            onClick={hook.intentarCerrar}
            aria-label="Cerrar drawer"
            sx={{ color: 'text.secondary', '&:hover': { color: 'text.primary' } }}
          >
            <X size={28} />
          </IconButton>
        </Box>

        {/* Body scrollable */}
        <Box sx={{ flex: 1, overflowY: 'auto', px: '36px', pt: '30px', pb: '24px' }}>
          {/* Sección empresa */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <Building2 size={18} color="#963781" />
            <Typography sx={{ fontWeight: 600, fontSize: '0.95rem', fontFamily: 'Poppins, sans-serif', color: 'text.primary' }}>
              Información de la empresa
            </Typography>
          </Box>

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: '20px 28px', mb: 4 }}>
            {/* Razón social — ocupa todo el ancho en modo creación (RN-001) */}
            <TextField
              label="Razón social"
              value={hook.empresa.razonSocial}
              onChange={(e) => hook.actualizarEmpresa('razonSocial', e.target.value)}
              required
              error={!!hook.empresaErrors.razonSocial}
              helperText={
                hook.empresaErrors.razonSocial ? (
                  <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <CircleAlert size={14} />
                    {hook.empresaErrors.razonSocial}
                  </Box>
                ) : undefined
              }
              inputProps={{ maxLength: 160, ref: razonSocialRef }}
              sx={hook.modoEdicion ? inputSx : { ...inputSx, gridColumn: { sm: '1 / -1' } }}
            />

            {hook.modoEdicion && (
              <>
                {/* NIT */}
                <TextField
                  label="NIT"
                  value={hook.empresa.nit}
                  onChange={(e) => hook.actualizarEmpresa('nit', e.target.value)}
                  error={!!hook.empresaErrors.nit}
                  helperText={
                    hook.empresaErrors.nit ? (
                      <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <CircleAlert size={14} />
                        {hook.empresaErrors.nit}
                      </Box>
                    ) : (
                      'Solo dígitos, 9-10 caracteres'
                    )
                  }
                  inputProps={{ maxLength: 10 }}
                  sx={inputSx}
                />

                {/* Actividad económica — span 2 */}
                <Box sx={{ gridColumn: { sm: '1 / -1' } }}>
                  <Autocomplete
                    options={actividades}
                    getOptionLabel={(a) => `${a.nombre}`}
                    value={actividadSeleccionada}
                    onChange={(_e, val) => hook.actualizarEmpresa('actividadEconomicaId', val?.id ?? null)}
                    popupIcon={<ChevronDown size={18} />}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Actividad económica"
                        error={!!hook.empresaErrors.actividadEconomicaId}
                        helperText={
                          hook.empresaErrors.actividadEconomicaId ? (
                            <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <CircleAlert size={14} />
                              {hook.empresaErrors.actividadEconomicaId}
                            </Box>
                          ) : undefined
                        }
                        InputProps={{
                          ...params.InputProps,
                          startAdornment: <Search size={16} color="#666" style={{ marginRight: 6 }} />,
                        }}
                        sx={inputSx}
                      />
                    )}
                  />
                </Box>

                {/* Gran contribuyente — span 2 */}
                <Box sx={{ gridColumn: { sm: '1 / -1' } }}>
                  <Typography sx={{ fontSize: '0.82rem', color: 'text.secondary', fontFamily: 'Poppins, sans-serif', mb: 0.75 }}>
                    Gran contribuyente
                  </Typography>
                  <Box
                    sx={{
                      display: 'inline-flex',
                      border: '1px solid',
                      borderColor: 'grey.300',
                      borderRadius: '6px',
                      overflow: 'hidden',
                      width: { xs: '100%', sm: '448px' },
                      height: '50px',
                    }}
                  >
                    {[
                      { label: 'No', value: false },
                      { label: 'Sí', value: true },
                    ].map(({ label, value }) => {
                      const activo = hook.empresa.granContribuyente === value;
                      return (
                        <Box
                          key={label}
                          onClick={() => hook.actualizarEmpresa('granContribuyente', value)}
                          sx={{
                            flex: 1,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer',
                            backgroundColor: activo ? '#963781' : 'transparent',
                            color: activo ? '#fff' : 'text.secondary',
                            fontFamily: 'Poppins, sans-serif',
                            fontSize: '0.9rem',
                            fontWeight: activo ? 600 : 400,
                            transition: 'background-color 0.15s',
                            userSelect: 'none',
                            '&:hover': { backgroundColor: activo ? '#7D2D6C' : 'grey.100' },
                          }}
                        >
                          {label}
                        </Box>
                      );
                    })}
                  </Box>
                </Box>
              </>
            )}

            {/* Departamento */}
            <FormControl error={!!hook.empresaErrors.departamentoId} sx={inputSx}>
              <InputLabel required>Departamento</InputLabel>
              <Select
                label="Departamento"
                value={hook.empresa.departamentoId ?? ''}
                onChange={(e) => hook.actualizarEmpresa('departamentoId', e.target.value ? Number(e.target.value) : null)}
                IconComponent={() => <ChevronDown size={18} style={{ marginRight: 8 }} />}
              >
                {departamentos.map((d) => (
                  <MenuItem key={d.id} value={d.id}>{d.nombre}</MenuItem>
                ))}
              </Select>
              {hook.empresaErrors.departamentoId && (
                <FormHelperText>
                  <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <CircleAlert size={14} />
                    {hook.empresaErrors.departamentoId}
                  </Box>
                </FormHelperText>
              )}
            </FormControl>

            {/* Ciudad */}
            <FormControl
              error={!!hook.empresaErrors.ciudadId}
              disabled={!hook.empresa.departamentoId}
              sx={inputSx}
            >
              <InputLabel required>Ciudad</InputLabel>
              <Select
                label="Ciudad"
                value={ciudadSeleccionada ? ciudadSeleccionada.id : ''}
                onChange={(e) => hook.actualizarEmpresa('ciudadId', e.target.value ? Number(e.target.value) : null)}
                IconComponent={() => <ChevronDown size={18} style={{ marginRight: 8 }} />}
              >
                {ciudades.map((c) => (
                  <MenuItem key={c.id} value={c.id}>{c.nombre}</MenuItem>
                ))}
              </Select>
              {hook.empresaErrors.ciudadId && (
                <FormHelperText>
                  <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <CircleAlert size={14} />
                    {hook.empresaErrors.ciudadId}
                  </Box>
                </FormHelperText>
              )}
            </FormControl>

            {/* Dirección — span 2 */}
            {hook.modoEdicion && (
              <TextField
                label="Dirección"
                value={hook.empresa.direccion}
                onChange={(e) => hook.actualizarEmpresa('direccion', e.target.value)}
                error={!!hook.empresaErrors.direccion}
                helperText={
                  hook.empresaErrors.direccion ? (
                    <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <CircleAlert size={14} />
                      {hook.empresaErrors.direccion}
                    </Box>
                  ) : undefined
                }
                inputProps={{ maxLength: 200 }}
                sx={{ ...inputSx, gridColumn: { sm: '1 / -1' } }}
              />
            )}
          </Box>

          {/* Sección contactos */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <UsersRound size={18} color="#963781" />
              <Typography sx={{ fontWeight: 600, fontSize: '0.95rem', fontFamily: 'Poppins, sans-serif', color: 'text.primary' }}>
                Contactos
              </Typography>
              {hook.contactos.length > 0 && (
                <Box
                  sx={{
                    minWidth: 20,
                    height: 20,
                    borderRadius: '10px',
                    backgroundColor: '#963781',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    px: 0.75,
                  }}
                >
                  <Typography sx={{ color: '#fff', fontSize: '0.7rem', fontWeight: 700 }}>
                    {hook.contactos.length}
                  </Typography>
                </Box>
              )}
            </Box>

            {!hook.editorAbierto && (
              <Button
                variant="outlined"
                size="small"
                startIcon={<Plus size={16} />}
                onClick={() => hook.abrirEditor()}
                sx={{
                  fontFamily: 'Poppins, sans-serif',
                  textTransform: 'none',
                  borderColor: '#963781',
                  color: '#963781',
                  '&:hover': { borderColor: '#7D2D6C', color: '#7D2D6C', backgroundColor: '#FBF5FA' },
                  borderRadius: '6px',
                }}
              >
                Agregar contacto
              </Button>
            )}
          </Box>

          {/* Error sin contactos */}
          {hook.submitError && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1.5 }}>
              <CircleAlert size={16} color="#d32f2f" />
              <Typography sx={{ fontSize: '0.82rem', color: 'error.main', fontFamily: 'Poppins, sans-serif' }}>
                {hook.submitError}
              </Typography>
            </Box>
          )}

          {/* Lista de contactos */}
          {hook.contactos.length > 0 && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 2 }}>
              {hook.contactos.map((c) => (
                <ContactCard
                  key={c.id}
                  contacto={c}
                  onEditar={hook.abrirEditor}
                  onEliminar={hook.eliminarContacto}
                />
              ))}
            </Box>
          )}

          {/* Editor inline */}
          {hook.editorAbierto && (
            <Box sx={{ mb: 2 }}>
              <ContactoEditor
                contacto={hook.contactoEditando}
                errors={hook.contactoErrors}
                onAgregar={hook.agregarContacto}
                onEditar={hook.editarContacto}
                onCancelar={hook.cerrarEditor}
              />
            </Box>
          )}

          {/* Hint punteado */}
          {!hook.editorAbierto && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                border: '1.5px dashed',
                borderColor: 'grey.300',
                borderRadius: '8px',
                p: '12px 16px',
                color: 'text.secondary',
              }}
            >
              <CirclePlus size={16} />
              <Typography sx={{ fontSize: '0.82rem', fontFamily: 'Poppins, sans-serif' }}>
                Puedes agregar más contactos después de crear el cliente
              </Typography>
            </Box>
          )}
        </Box>

        {/* Footer sticky */}
        <Box
          sx={{
            minHeight: 82,
            flexShrink: 0,
            borderTop: '1px solid',
            borderColor: 'grey.200',
            px: '36px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
          }}
        >
          <Typography sx={{ fontSize: '0.85rem', color: 'text.secondary', fontFamily: 'Poppins, sans-serif' }}>
            {hook.contactos.length === 0
              ? 'Sin contactos agregados'
              : hook.contactos.length === 1
              ? '1 contacto agregado'
              : `${hook.contactos.length} contactos agregados`}
          </Typography>

          <Box sx={{ display: 'flex', gap: 1.5 }}>
            <Button
              variant="text"
              onClick={hook.intentarCerrar}
              sx={{
                fontFamily: 'Poppins, sans-serif',
                textTransform: 'none',
                color: 'text.secondary',
                height: 50,
                px: 2,
              }}
            >
              Cancelar
            </Button>
            <Button
              variant="contained"
              onClick={hook.submit}
              disabled={hook.isSubmitting}
              endIcon={hook.isSubmitting ? <CircularProgress size={16} color="inherit" /> : undefined}
              sx={{
                fontFamily: 'Poppins, sans-serif',
                textTransform: 'none',
                backgroundColor: '#963781',
                '&:hover': { backgroundColor: '#7D2D6C' },
                '&:disabled': { backgroundColor: '#ccc' },
                height: 50,
                px: 3,
                borderRadius: '6px',
                minWidth: 140,
              }}
            >
              {hook.isSubmitting
                ? hook.modoEdicion ? 'Guardando...' : 'Creando...'
                : hook.modoEdicion ? 'Guardar cambios' : 'Crear cliente'}
            </Button>
          </Box>
        </Box>
      </Box>

      {/* Diálogo: confirmar descartar cambios */}
      <Dialog
        open={hook.confirmDescartar}
        onClose={hook.cancelarDescartar}
        PaperProps={{ sx: { borderRadius: '12px', p: 1, minWidth: 360 } }}
      >
        <DialogTitle sx={{ fontFamily: 'Poppins, sans-serif', fontWeight: 700 }}>
          ¿Descartar cambios?
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontFamily: 'Poppins, sans-serif', fontSize: '0.9rem', color: 'text.secondary' }}>
            Los datos ingresados se perderán.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={hook.cancelarDescartar}
            sx={{ fontFamily: 'Poppins, sans-serif', textTransform: 'none', color: 'text.secondary' }}
          >
            Volver
          </Button>
          <Button
            onClick={hook.confirmarDescartar}
            variant="contained"
            sx={{
              fontFamily: 'Poppins, sans-serif',
              textTransform: 'none',
              backgroundColor: '#963781',
              '&:hover': { backgroundColor: '#7D2D6C' },
              borderRadius: '6px',
            }}
          >
            Descartar
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

const inputSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '6px',
    fontFamily: 'Poppins, sans-serif',
    minHeight: '62px',
  },
  '& .MuiInputLabel-root': {
    fontFamily: 'Poppins, sans-serif',
  },
  '& .MuiSelect-select': {
    fontFamily: 'Poppins, sans-serif',
  },
};
